import { useEffect, useState } from "react";
import { Button, Input, Modal, Select, Textarea } from "@legal-500/legal500-design-system";
import {
  DATA_CLASSIFICATIONS,
  DATA_CLASSIFICATION_LABELS,
  type AiMarkdownTemplate,
  type AiSchema,
  type CreateAiMarkdownTemplateInput,
  type DataClassification,
} from "@/api/aiParser.service";
import { Toggle } from "@/components/Toggle/Toggle";
import styles from "./AiMarkdownTemplates.module.css";

export const FILENAME_PLACEHOLDERS = ["{name}", "{firm}", "{submissionId}", "{fileName}"];

const STARTER_BODY = `# <%= doc.head.firmName %>

<% doc.matters.forEach(function (matter) { %>
- <%= matter.clientName %>
<% }); %>
`;

type FormState = {
  aiSchemaId: string;
  name: string;
  filenameFormat: string;
  body: string;
  dataClassification: DataClassification;
  enabled: boolean;
};

function emptyForm(schemas: AiSchema[]): FormState {
  return {
    aiSchemaId: schemas[0]?.id ?? "",
    name: "",
    filenameFormat: "{name}.md",
    body: STARTER_BODY,
    // Starts where the schema's default is, rather than inheriting the selected schema's value:
    // a template is usually written to be *less* sensitive than the extraction, and starting at
    // the restrictive end makes relaxing it a deliberate act.
    dataClassification: "confidential",
    enabled: true,
  };
}

function formFromTemplate(template: AiMarkdownTemplate): FormState {
  return {
    aiSchemaId: template.aiSchemaId,
    name: template.name,
    filenameFormat: template.filenameFormat,
    body: template.body,
    dataClassification: template.dataClassification,
    enabled: template.enabled,
  };
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.aiSchemaId) errors.aiSchemaId = "Pick the schema this template renders";
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.filenameFormat.trim()) errors.filenameFormat = "Filename format is required";
  if (!form.body.trim()) errors.body = "The EJS body cannot be empty";
  return errors;
}

type Props = {
  isOpen: boolean;
  template: AiMarkdownTemplate | null;
  schemas: AiSchema[];
  saving: boolean;
  saveError: string | null;
  onClose: () => void;
  onSubmit: (input: CreateAiMarkdownTemplateInput) => void;
};

export function TemplateFormModal({
  isOpen,
  template,
  schemas,
  saving,
  saveError,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<FormState>(() => emptyForm(schemas));
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (!isOpen) return;
    setForm(template ? formFromTemplate(template) : emptyForm(schemas));
    setErrors({});
  }, [isOpen, template, schemas]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(previous => ({ ...previous, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit({
      aiSchemaId: form.aiSchemaId,
      name: form.name.trim(),
      filenameFormat: form.filenameFormat.trim(),
      dataClassification: form.dataClassification,
      body: form.body,
      enabled: form.enabled,
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={template ? `Edit ${template.name}` : "New markdown template"}
      size="large"
      backdropClick={false}
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.formGrid}>
          <Input
            label="Name"
            value={form.name}
            onChange={event => set("name", event.target.value)}
            error={errors.name}
            fullWidth
          />
          <Select
            label="Schema"
            value={form.aiSchemaId}
            onChange={event => set("aiSchemaId", event.target.value)}
            error={errors.aiSchemaId}
            options={[
              { value: "", label: "Select a schema…" },
              ...schemas.map(schema => ({ value: schema.id, label: schema.name })),
            ]}
            helperText="`doc` in the template is the JSON this schema produced"
            fullWidth
          />
        </div>

        <Input
          label="Filename format"
          value={form.filenameFormat}
          onChange={event => set("filenameFormat", event.target.value)}
          error={errors.filenameFormat}
          fullWidth
        />
        <p className={styles.placeholderHelp}>
          Placeholders:{" "}
          {FILENAME_PLACEHOLDERS.map(placeholder => (
            <code key={placeholder} className={styles.placeholderCode}>
              {placeholder}
            </code>
          ))}
        </p>

        <Select
          label="Data classification"
          value={form.dataClassification}
          onChange={event => set("dataClassification", event.target.value as DataClassification)}
          options={DATA_CLASSIFICATIONS.map(value => ({
            value,
            label: DATA_CLASSIFICATION_LABELS[value],
          }))}
          fullWidth
        />
        <p className={styles.placeholderHelp}>
          This template&rsquo;s own label, not the schema&rsquo;s. The rendered .md and .pdf are
          written into a folder of this name inside the run, so a summary that is safe to circulate
          does not have to sit beside the extraction it was made from.
        </p>

        <div className={styles.enableRow}>
          <Toggle
            checked={form.enabled}
            onChange={next => set("enabled", next)}
            ariaLabel="Enabled"
          />
          <span className={styles.enableLabel}>
            {form.enabled
              ? "Enabled — rendered on every run of its schema"
              : "Disabled — nothing is rendered"}
          </span>
        </div>

        <Textarea
          label="EJS body"
          value={form.body}
          onChange={event => set("body", event.target.value)}
          rows={18}
          spellCheck={false}
          className={styles.monospaceField}
          error={errors.body}
          helperText="EJS, rendered server-side against `doc` — the JSON of the schema above."
          fullWidth
        />

        {saveError && (
          <div className={styles.errorBanner} role="alert">
            {saveError}
          </div>
        )}

        <div className={styles.formActions}>
          <Button type="button" variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Saving…" : template ? "Save changes" : "Create template"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
